/**
 * ERP & Treuhandbüro Data Portability / Connector Module (`web/src/components/ui.tsx`).
 *
 * @statutory OR Art. 957a Abs. 2 Ziff. 1–5 (Vollständige & nachprüfbare Buchführung) · GeBüV Art. 6–10 (Datenübernahme & Prüfspur) · MWSTG Art. 70 · Schweizer Kontenrahmen KMU
 */
import { useEffect, useLayoutEffect, useRef, type ReactNode, type ComponentType } from 'react';
import { createPortal } from 'react-dom';
import { LoaderCircle, X, TriangleAlert, RotateCcw } from 'lucide-react';
import { autosize } from '../vendor/autosize.js';
import { pct } from '../lib/format';
import { t } from '../lib/i18n';

/** Monochrome icon: stroke inherits currentColor. */
export function I({ icon: C, sm, spin, className = '' }: { icon: ComponentType<any>; sm?: boolean; spin?: boolean; className?: string }) {
  return <C aria-hidden className={`icon ${sm ? 'sm' : ''} ${spin ? 'spin' : ''} ${className}`} strokeWidth={1.6} />;
}

export function Spinner({ sm = true }: { sm?: boolean }) { return <I icon={LoaderCircle} sm={sm} spin />; }

export function Badge({ kind, children, dot }: { kind?: 'accent' | 'pos' | 'warn' | 'neg'; children: ReactNode; dot?: boolean }) {
  return <span className={`badge ${kind ?? ''}`}>{dot && <i className="dot" />}{children}</span>;
}

export function Confidence({ value }: { value: number }) {
  const cls = value >= 0.9 ? 'high' : value < 0.7 ? 'low' : '';
  return (
    <span className={`confidence ${cls}`} title={t('ui.confidence')}>
      <span className="bar"><i style={{ width: `${Math.round(value * 100)}%` }} /></span>
      <span className="num">{pct(value)}</span>
    </span>
  );
}

export function Skeleton({ h = 16, w = '100%' }: { h?: number; w?: number | string }) {
  return <div className="skeleton" style={{ height: h, width: w }} />;
}

export function LoadingRows({ rows = 6 }: { rows?: number }) {
  return <div className="card-pad stack-sm" aria-busy="true">{Array.from({ length: rows }, (_, i) => <Skeleton key={i} h={14} w={`${70 + ((i * 37) % 30)}%`} />)}</div>;
}

export function Empty({ icon, title, children, action }: { icon: ComponentType<any>; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <div className="icon-wrap"><I icon={icon} /></div>
      <h4>{title}</h4>
      {children && <p className="muted" style={{ maxWidth: '44ch' }}>{children}</p>}
      {action}
    </div>
  );
}

export function ErrorCard({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="error-card" role="alert">
      <I icon={TriangleAlert} />
      <div className="grow">{message}</div>
      {onRetry && <button className="btn sm" onClick={onRetry}><I icon={RotateCcw} sm /> {t('ui.retry')}</button>}
    </div>
  );
}

/** Dialog shell: Esc closes, focus is trapped, first field autofocused, focus returns on close. */
function useDialog(onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const back = document.activeElement as HTMLElement | null;
    const f = requestAnimationFrame(() => requestAnimationFrame(() => {
      const first = ref.current?.querySelector<HTMLElement>('[data-autofocus], input:not([type=hidden]):not([disabled]), textarea, [data-combo-trigger]');
      (first ?? ref.current)?.focus({ preventScroll: true });
    }));
    return () => { cancelAnimationFrame(f); back?.focus?.({ preventScroll: true }); };
  }, []);
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) { e.preventDefault(); onClose(); }
      if (e.key === 'Tab' && ref.current) {
        const els = [...ref.current.querySelectorAll<HTMLElement>('button:not(:disabled), [href], input:not(:disabled), textarea, [tabindex]:not([tabindex="-1"])')];
        if (!els.length) return;
        const first = els[0], last = els[els.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
  return ref;
}

export function Modal({ title, onClose, children, footer, className = '' }: { title?: string; onClose: () => void; children: ReactNode; footer?: ReactNode; className?: string }) {
  const ref = useDialog(onClose);
  return createPortal(
    <>
      <div className="scrim" onClick={onClose} />
      <div ref={ref} className={`modal ${className}`} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1}>
        {title && <div className="drawer-head"><h3>{title}</h3><button className="btn ghost icon-only" onClick={onClose} aria-label={t('ui.close')}><I icon={X} /></button></div>}
        {children}
        {footer && <div className="acard-foot" style={{ justifyContent: 'flex-end' }}>{footer}</div>}
      </div>
    </>,
    document.body,
  );
}

export function Drawer({ title, subtitle, onClose, children, actions }: { title: ReactNode; subtitle?: ReactNode; onClose: () => void; children: ReactNode; actions?: ReactNode }) {
  const ref = useDialog(onClose);
  return createPortal(
    <>
      <div className="scrim" onClick={onClose} />
      <aside ref={ref} className="drawer" role="dialog" aria-modal="true" tabIndex={-1}>
        <div className="drawer-head">
          <div style={{ flex: 1, minWidth: 0 }}><h3>{title}</h3>{subtitle && <div className="muted" style={{ marginTop: 4 }}>{subtitle}</div>}</div>
          {actions}
          <button className="btn ghost icon-only" onClick={onClose} aria-label={t('ui.close')}><I icon={X} /></button>
        </div>
        <div className="drawer-body">{children}</div>
      </aside>
    </>,
    document.body,
  );
}

/** A field is as tall as its content (Avanti self-sizing fields). */
export function AutoTextarea({ value, onChange, minRows = 2, maxRows = 12, className = 'textarea', inputRef, ...rest }: {
  value: string; onChange: (v: string) => void; minRows?: number; maxRows?: number; className?: string; inputRef?: React.RefObject<HTMLTextAreaElement | null>;
} & Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'>) {
  const local = useRef<HTMLTextAreaElement>(null);
  const ref = inputRef ?? local;
  const handle = useRef<ReturnType<typeof autosize> | null>(null);
  useEffect(() => {
    if (!ref.current) return;
    handle.current = autosize(ref.current, { minRows, maxRows });
    return () => handle.current?.destroy();
  }, [minRows, maxRows, ref]);
  useEffect(() => { handle.current?.fit(); }, [value]);
  return <textarea ref={ref as any} className={className} value={value} onChange={(e) => onChange(e.target.value)} rows={minRows} {...rest} />;
}

export function Kbd({ children }: { children: ReactNode }) { return <kbd>{children}</kbd>; }
